/* 動画ヒーローの順次クロスフェード(2026-08-17)
   参考: 西芳寺のTOP。複数の動画が一定間隔で静かに入れ替わる。

   - 表示時間はセクション設定(slide_seconds)ではなく各動画の実尺を優先し、
     終わる1.6秒前から次をフェードインする(尺が取れない場合は設定値)
   - 動画は muted + playsinline(iOSの自動再生条件)
   - prefers-reduced-motion では何もしない(CSSがポスター静止画へ切り替える)
   - 画面外ではタイマーと再生を止める */

if (!customElements.get('ny-soda-hero-videos')) {
  class NySodaHeroVideos extends HTMLElement {
    connectedCallback() {
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videos = Array.from(this.querySelectorAll('.ny-soda-hero-video__video'));
      this.FADE = 1.6; // CSSのtransitionと合わせる
      this.active = 0;
      this.timer = 0;
      this.visible = false;

      // ヘッダー分を差し引いて、初期画面にちょうど収める
      this.fitHeight();
      // スマホはURLバーの開閉だけでもresizeが飛ぶ。高さを測り直すと
      // ヒーローが伸び縮みして見えるため、横幅が変わったとき
      // (回転・ウィンドウリサイズ)だけ追従する
      this.lastWidth = window.innerWidth;
      this.onResize = () => {
        if (window.innerWidth === this.lastWidth) return;
        this.lastWidth = window.innerWidth;
        this.fitHeight();
      };
      addEventListener('resize', this.onResize, { passive: true });

      if (this.reduce.matches || this.videos.length === 0) return;

      // 1本目を確実にアクティブへ(ブロック設定由来のvideo_tagはクラス無しのため)
      this.videos.forEach((v, i) => {
        v.classList.toggle('is-active', i === 0);
        v.muted = true; // 属性が落ちても確実に
      });

      // オープニング演出中は幕の裏で時間を消費しないよう待機し、
      // 幕が抜け始める瞬間に1本目を頭から再生する(2026-08-24)。
      // 演出なし(クラスが付かない)ならそのまま通常動作
      this.introPending = this.classList.contains('ny-intro-pending');
      if (this.introPending) {
        this.introObserver = new MutationObserver(() => {
          const revealing = this.classList.contains('ny-intro-revealing');
          const pending = this.classList.contains('ny-intro-pending');
          if (!revealing && pending) return;
          this.introPending = false;
          this.introObserver.disconnect();
          try {
            this.videos[this.active].currentTime = 0;
          } catch (e) {
            /* メタデータ未読込なら頭からで良い */
          }
          if (this.visible) {
            this.play(this.active);
            this.schedule();
          }
        });
        this.introObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
      }

      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !this.introPending) {
          this.play(this.active);
          this.schedule();
        } else if (!this.visible) {
          clearTimeout(this.timer);
          this.videos.forEach((v) => v.pause());
        }
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      removeEventListener('resize', this.onResize);
      if (this.observer) this.observer.disconnect();
      if (this.introObserver) this.introObserver.disconnect();
    }

    fitHeight() {
      // スクロール中は固定ヘッダーのせいで実測値が壊れる。
      // その場合は前回の値を保つ(初回はscrollY=0で必ず正しく測れる)
      if (window.scrollY > 1 && this.style.getPropertyValue('--ny-hero-offset')) return;
      const header = document.querySelector('.section-header');
      const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom + window.scrollY) : 0;
      this.style.setProperty('--ny-hero-offset', `${Math.round(bottom)}px`);
    }

    play(i) {
      const v = this.videos[i];
      if (!v) return;
      const p = v.play();
      if (p) p.catch(() => {});
    }

    holdSeconds(i) {
      // セクション設定(slide_seconds)は最低表示秒数として扱う。
      // 動画の実尺がそれより長ければ実尺まで見せる(2026-08-24、
      // 切替が早すぎるとの指摘で設定値を下限に昇格。短い動画はloopで持たせる)
      const setting = Number(this.dataset.slideSeconds || 5);
      const v = this.videos[i];
      let base = setting;
      if (v && Number.isFinite(v.duration) && v.duration > this.FADE + 0.5) {
        base = Math.max(v.duration, setting);
      }
      return Math.max(2, base - this.FADE);
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.videos.length < 2) return;
      this.timer = setTimeout(() => this.next(), this.holdSeconds(this.active) * 1000);
    }

    next() {
      if (!this.visible) return;
      const from = this.active;
      const to = (from + 1) % this.videos.length;
      const prevVideo = this.videos[from];
      const nextVideo = this.videos[to];
      try {
        nextVideo.currentTime = 0;
      } catch (e) {
        /* メタデータ未読込なら頭からで良い */
      }
      this.play(to);
      // 前の動画は is-leaving で不透明のまま下の層に残す。
      // 両方を半透明にすると背景の黒が透けて一瞬暗く沈むため
      prevVideo.classList.add('is-leaving');
      prevVideo.classList.remove('is-active');
      nextVideo.classList.add('is-active');
      this.active = to;
      // フェード完了後に前の動画を退場させて止める(新しい動画の裏なので見えない)
      setTimeout(() => {
        prevVideo.classList.remove('is-leaving');
        prevVideo.pause();
      }, this.FADE * 1000 + 100);
      this.schedule();
    }
  }

  customElements.define('ny-soda-hero-videos', NySodaHeroVideos);
}
